import React, { useEffect, useState, useCallback } from "react";
import { connect, useDispatch } from "react-redux";
import { FileText, Printer, Loader2, Copy, ArchiveRestore, Archive, ShieldCheck } from "lucide-react";

// UI Components
import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader } from "components/ui/card";

// Store and Utils
import fetchRequest from "store/thunks/fetchRequest";
import { ParseTime } from "utils/parseTime";
import { NEW_REQUEST_PAGE } from "utils/reducers";

// Components
import RequestForm from "./components/RequestForm";
import VerificationWindow from "./components/VerificationWindow";
import { printRequestDetails } from "./utils/printUtils";

interface RequestDetails {
  inquiry_type: string;
  inquiry_status: string;
  patient_name: string;
  inquiry_summary: string;
  inquiry_creation_ts: string;
  specialty: string;
  [key: string]: any;
}

interface RequestProps {
  requestDetails: RequestDetails | null;
  loading: boolean;
  error: string | null;
  fetchRequest: (requestId: string) => void;
  requestID: string;
}

const Request: React.FC<RequestProps> = ({
  requestDetails,
  loading,
  error,
  fetchRequest,
  requestID,
}) => {
  const [printData, setPrintData] = useState<any>(null);
  const [isPrefilling, setIsPrefilling] = useState<boolean>(false);
  const [showVerification, setShowVerification] = useState<boolean>(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (requestID) {
      fetchRequest(requestID);
    }
  }, [fetchRequest, requestID]);

  const handlePrintDataReady = useCallback((data: {
    tableResults: any;
    eligibilitySearchJson: any;
    questionTextMap: Record<string, string>;
  }) => {
    setPrintData(data);
  }, []);

  const handlePrint = () => {
    // If dental request, use old print, otherwise, use new print

    if (!requestID || !requestDetails) return;

    let useOldPrint = requestDetails.specialty === "DENTAL" && requestDetails.inquiry_type === "ELIGIBILITY_SEARCH" && requestDetails.inquiry_status === "SUCCESS";
    if (useOldPrint) {
      const printUrl = `/dashboard/data/eligibility_search/${requestID}/print`;
      window.open(printUrl, "_blank", "noopener,noreferrer");
    }
    else {
      // Use unified print for all request types.
      printRequestDetails({
        requestDetails,
        patientName: requestDetails.patient_name,
        subject: requestDetails.inquiry_summary,
        topic: requestDetails.inquiry_type,
        status: requestDetails.inquiry_status,
        time: ParseTime(requestDetails.inquiry_creation_ts),
        requestID: requestID,
        tableResults: printData?.tableResults,
        eligibilitySearchJson: printData?.eligibilitySearchJson,
        questionTextMap: printData?.questionTextMap,
      });
    }



    requestDetails.specialty === "DENTAL" ? handlePrintDataReady(printData) : handlePrintDataReady(printData);

    // Simple backend print URL approach like the old version
    const printUrl = `/dashboard/data/eligibility_search/${requestID}/print`;
    window.open(printUrl, "_blank", "noopener,noreferrer");
  };

  const handlePrefillNewRequest = async () => {
    if (!requestID) return;

    setIsPrefilling(true);
    try {
      // Fetch the complete request data from the API
      const response = await fetch(`/dashboard/data/${requestID}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch request data: ${response.status}`);
      }

      const requestData = await response.json();

      // Extract relevant data from the fetched request to prefill a new request
      const prefillData = {
        type: requestData.inquiry_type || undefined,
        insurance_name: requestData.insurance_name || undefined,
        practice_id: requestData.practice_id || undefined,
        billing_provider_id: requestData.billing_provider_id || undefined,
        rendering_provider_id: requestData.rendering_provider_id || undefined,
        tax_id: requestData.tax_id || undefined,
        insurance_in_network: requestData.insurance_in_network,
        npi: requestData.npi || undefined,
        eligibility_search_queries: requestData.eligibility_search_queries || undefined,
        place_of_service: requestData.place_of_service || undefined,
        is_specialist: requestData.is_specialist || undefined,
        patient_name: requestData.patient_name || undefined,
        dob: requestData.patient_dob || undefined,
        patient_address: requestData.patient_address || undefined,
        member_id: requestData.patient_member_id || undefined,
        group_id: requestData.patient_group_id || undefined,
        additional_info: requestData.additional_info || undefined,
        diagnosis_codes: requestData.diagnosis_codes || undefined,
        procedure_codes: requestData.procedure_codes || undefined,
        claims_date_of_service: requestData.claims_date_of_service || undefined,
        billed_amount: requestData.billed_amount || undefined,
      };

      // Store the prefill data in localStorage
      localStorage.setItem("newRequestFormData", JSON.stringify(prefillData));

      // Navigate to new request page
      dispatch({ type: NEW_REQUEST_PAGE.display });
    } catch (error) {
      console.error("Error fetching request data for prefill:", error);
      // You might want to show a toast notification here
      alert("Failed to load request data for prefill. Please try again.");
    } finally {
      setIsPrefilling(false);
    }
  };

  const handleToggleArchive = async () => {
    if (!requestID || !requestDetails) return;
    const currentlyArchived = !!requestDetails.archived;
    const route = currentlyArchived ? `/dashboard/unarchive/${requestID}` : `/dashboard/archive/${requestID}`;
    try {
      const response = await fetch(route, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        throw new Error(`Failed to ${currentlyArchived ? 'unarchive' : 'archive'} request`);
      }
      // Reload to refresh inbox and request view
      window.location.reload();
    } catch (e) {
      console.error(e);
      alert(`Unable to ${currentlyArchived ? 'unarchive' : 'archive'} this request. Please try again.`);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="p-4 bg-[#005151]/10 rounded-full mb-4">
              <Loader2 className="h-8 w-8 animate-spin text-[#005151]" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Loading request details
            </h3>
            <p className="text-gray-600">
              Please wait while we fetch the request information...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="shadow-lg border-0 bg-red-50">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="p-4 bg-red-100 rounded-full mb-4">
              <FileText className="h-8 w-8 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-red-900 mb-2">
              Error loading request
            </h3>
            <p className="text-red-700">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!requestDetails) {
    return (
      <div className="p-6">
        <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="p-4 bg-gray-100 rounded-full mb-4">
              <FileText className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No request selected
            </h3>
            <p className="text-gray-600">
              Please select a request to view its details.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Request Content */}
      <Card className="border-0 bg-white shadow-none">
        <CardHeader>
          {/* Hero Section with Print Button */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-[#005151] rounded-xl">
                <FileText className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Request Details -- {requestDetails.patient_name}
                </h1>
                <p className="text-gray-600 mt-1">View request information</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                onClick={() => setShowVerification(true)}
                variant="outline"
                size="lg"
                className="border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white"
                disabled={!requestDetails}
              >
                <ShieldCheck className="h-5 w-5 mr-2" />
                View Verification
              </Button>
              <Button
                onClick={handleToggleArchive}
                variant="outline"
                size="lg"
                className="border-gray-400 text-gray-700 hover:bg-gray-700 hover:text-white"
                disabled={!requestDetails}
              >
                {requestDetails?.archived ? (
                  <ArchiveRestore className="h-5 w-5 mr-2" />
                ) : (
                  <Archive className="h-5 w-5 mr-2" />
                )}
                {requestDetails?.archived ? 'Unarchive' : 'Archive'}
              </Button>
              <Button
                onClick={handlePrefillNewRequest}
                variant="outline"
                size="lg"
                className="border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white"
                disabled={!requestDetails || isPrefilling}
              >
                <Copy className="h-5 w-5 mr-2" />
                {isPrefilling ? "Loading..." : "Prefill New Request"}
              </Button>
              <Button
                onClick={handlePrint}
                variant="outline"
                size="lg"
                className="border-[#005151] text-[#005151] hover:bg-[#005151] hover:text-white"
                disabled={!requestDetails}
              >
                <Printer className="h-5 w-5 mr-2" />
                Print
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <RequestForm
            requestDetails={requestDetails}
            patientName={requestDetails.patient_name}
            subject={requestDetails.inquiry_summary}
            topic={requestDetails.inquiry_type}
            status={requestDetails.inquiry_status}
            time={ParseTime(requestDetails.inquiry_creation_ts)}
            showRequestID={requestID}
            onPrintDataReady={handlePrintDataReady}
          />
        </CardContent>
      </Card>

      {/* Verification Window */}
      <VerificationWindow
        open={showVerification}
        onOpenChange={setShowVerification}
        inquiryId={requestID}
      />
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  loading: state.fetchRequest.loading,
  requestDetails: state.fetchRequest.requestDetails,
  error: state.fetchRequest.requestError,
  requestID: state.fetchRequest.requestID,
});

const mapDispatchToProps = {
  fetchRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(Request);
